import * as React from "react";
import { Checkbox, Input, InputRef, Modal, notification } from "antd";
import { linksOf, listAndDelete, request } from "../../services/cpln";
import { useNavigate } from "react-router-dom";
import { Gvc } from "../../schema/types/gvc/entity";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { selectOrg, setGvc } from "../../store/contextSlice";
import { fetchGvcs } from "../../store/userDataSlice";
import { gvcApi } from "../../store/api/gvcApi";

interface Props {
  gvc: Gvc;
  onClose: any;
  onDone: any;
  visible: boolean;
}

export const DeleteModal: React.FC<Props> = ({ gvc, onClose, onDone, visible }) => {
  const dispatch = useAppDispatch();
  const org = useAppSelector(selectOrg);

  const navigate = useNavigate();
  const [confirmationText, setConfirmationText] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isForceDelete, setIsForceDelete] = React.useState(false);
  const inputRef = React.useRef<InputRef>(null);

  const canDelete = confirmationText === gvc.name;

  async function confirmDelete() {
    try {
      setIsLoading(true);

      // Delete resources related to GVC
      if (isForceDelete) {
        const context = { org: org, gvc: gvc.name };

        await listAndDelete("workload", context);
        await listAndDelete("volumeset", context);
        await listAndDelete("identity", context);
      }

      // Delete GVC
      await request({ method: "delete", url: linksOf(gvc).self! });
      dispatch(gvcApi.util.invalidateTags([{ type: "Gvc", id: linksOf(gvc).self! }]));

      await dispatch(fetchGvcs());
      dispatch(setGvc(""));
      navigate(`/console/org/${org}/gvc`);
      onDone();
      notification.success({
        message: "Success",
        description: `Deleted`,
      });
    } catch (e) {
      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed to delete",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  return (
    <Modal
      title={
        <>
          Delete GVC:{" "}
          <span className="font-bold" style={{ color: "var(--color-danger)" }}>
            {gvc.name}
          </span>
        </>
      }
      open={visible}
      maskClosable={false}
      onCancel={onClose}
      onOk={confirmDelete}
      okText={`Delete`}
      cancelButtonProps={{ disabled: isLoading, type: "default", danger: false }}
      okButtonProps={{ disabled: isLoading || !canDelete, loading: isLoading, type: "primary", danger: true }}
      destroyOnHidden
      afterOpenChange={(open) => {
        if (open && inputRef.current) {
          inputRef.current.focus();
        }
      }}
    >
      <div className="flex flex-col gap-2">
        <span>Are you sure you want to delete this GVC? This action cannot be undone.</span>
        <div className={`mb-2`}>
          <div className={`flex items-center`}>
            <Checkbox
              checked={isForceDelete}
              onChange={(e) => setIsForceDelete(e.target.checked)}
              data-testid="gvc-force-delete"
            >
              Force Delete
            </Checkbox>
          </div>
          <div className="text-sm">
            If this is enabled, Control Plane will force delete volume sets and their volumes before deleting the GVC.
          </div>
        </div>
        <div className="delete-modal-confirmation-warning mt-4">{`Type "${gvc.name}" for confirming the delete action`}</div>
        <Input
          ref={inputRef}
          value={confirmationText}
          onChange={(e) => setConfirmationText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && canDelete && !isLoading) {
              confirmDelete();
            }
          }}
          placeholder={`confirmation`}
        />
      </div>
    </Modal>
  );
};
